import {
  Conditional,
  DealerLocatorMapContainer,
  DealerStore,
  useBreakpoint,
  useDrawer
} from 'components/pcc-components';
import { GoogleMapsLoader } from 'components/google-maps-loader';
import ErrorBoundaryWrapper from 'components/error-boundary';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { Coordinates } from 'app/[lang]/[esite]/(core)/_header/hooks/use-locations';
import { useFormContext } from 'react-hook-form';
import { DEFAULT_NUM_MAP_RESULTS, SELECTED_STORE_ID } from '../constants';
import { sortClosestVisibleMapStores } from '../utils';
import { useDealerLocatorLoader } from 'components/dealer-locator/dealer-locator-loader-context';
import { Filters } from '../types';
import { useEsiteCtx } from 'services/esite/context';
import { PAGE_BREAKPOINTS } from 'constants/common';
import DealerLocatorTogglesAndFilter from '../dealer-locator-toggle-and-filter/dealer-locator-toggle-filter';

interface DealerLocatorOptionsWrapperProps {
  allFilteredDealerStoresByDistance: DealerStore[];
  filteredAndOrderedVisibleDealerStores: DealerStore[];
  locations: DealerStore[];
  onSearchThisAreaClick: (center: Coordinates) => void;
  isMapToggleChecked: boolean;
  setIsMapToggleChecked: (toggle: boolean) => void;
  isExtendMapToggleChecked: boolean;
  setIsExtendMapToggleChecked: (toggle: boolean) => void;
  isLoading: boolean;
  isGetLocationsError?: boolean;
  showNoStoresMapErrorMessage?: boolean;
  showNetworkErrorMessage: boolean;
  filters: Filters;
  areFiltersLoading: boolean;
  setAreFiltersLoading: (value: boolean) => void;
  setShouldCloseEntireDL: (value: boolean) => void;
  filtersDrawer: ReturnType<typeof useDrawer>;
  isHidingMaps: boolean;
  showInCountryToggle?: boolean;
  isStoreLocationListViewUpdateFlag?: boolean;
  isStoreLocationPinEnhancementFlag?: boolean;
  isStoreLocationMapViewUpdateFlag?: boolean;
  isFullMapMobileFeatureFlag?: boolean;
}

export const DealerLocatorOptionsWrapper = ({
  locations,
  showInCountryToggle,
  allFilteredDealerStoresByDistance,
  filteredAndOrderedVisibleDealerStores,
  onSearchThisAreaClick,
  isMapToggleChecked,
  setIsMapToggleChecked,
  isLoading,
  isGetLocationsError,
  showNoStoresMapErrorMessage,
  areFiltersLoading,
  setAreFiltersLoading,
  filters,
  setShouldCloseEntireDL,
  filtersDrawer,
  showNetworkErrorMessage,
  isHidingMaps,
  isExtendMapToggleChecked,
  setIsExtendMapToggleChecked,
  isStoreLocationListViewUpdateFlag,
  isStoreLocationPinEnhancementFlag = false,
  isStoreLocationMapViewUpdateFlag = false,
  isFullMapMobileFeatureFlag = false
}: DealerLocatorOptionsWrapperProps) => {
  const t = useTranslations();
  const { watch } = useFormContext();
  const { userCountryCode, dealerCode } = useEsiteCtx();
  const { isDealerLocatorLoading } = useDealerLocatorLoader();
  const selectedStoreId = watch(SELECTED_STORE_ID);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const filteredAndOrderedVisibleMapDealerStores = useMemo(
    () =>
      allFilteredDealerStoresByDistance
        ? sortClosestVisibleMapStores(
            allFilteredDealerStoresByDistance?.slice(0, DEFAULT_NUM_MAP_RESULTS)
          )
        : undefined,
    [allFilteredDealerStoresByDistance]
  );

  const selectedStore = useMemo(
    () =>
      allFilteredDealerStoresByDistance?.find(
        (store) => store.id === Number(selectedStoreId)
      ),
    [selectedStoreId, allFilteredDealerStoresByDistance]
  );

  // Close the filters drawer when there are no errors applying filters
  useEffect(() => {
    if (filteredAndOrderedVisibleDealerStores.length && areFiltersLoading) {
      filtersDrawer.closeDrawer();
      setAreFiltersLoading(false);
      setShouldCloseEntireDL(true);
    }
  }, [
    filteredAndOrderedVisibleDealerStores,
    filtersDrawer,
    areFiltersLoading,
    setAreFiltersLoading,
    setShouldCloseEntireDL
  ]);

  // Turn map toggle off if there is a dealer locator service error or if the map search doesn't find a store
  useEffect(() => {
    if (
      isGetLocationsError ||
      showNoStoresMapErrorMessage ||
      showNetworkErrorMessage
    ) {
      setIsMapToggleChecked(false);
    }
  }, [
    isGetLocationsError,
    showNoStoresMapErrorMessage,
    showNetworkErrorMessage,
    setIsMapToggleChecked
  ]);

  const handleMapToggle = () => {
    setIsMapToggleChecked(!isMapToggleChecked);
  };

  const filtersCounter = Object.values(filters || {}).filter(
    (filterValue) => filterValue === true
  ).length;

  const showOptions = !!locations?.length || isLoading;

  const dealerLocatorToggleAndFilterContainer = () => {
    const storeFeatureFlag = {
      isStoreLocationMapViewUpdateFlag,
      isStoreLocationListViewUpdateFlag
    };
    return (
      <DealerLocatorTogglesAndFilter
        showOptions={showOptions}
        isHidingMaps={isHidingMaps}
        isMapToggleChecked={isMapToggleChecked}
        handleMapToggle={handleMapToggle}
        isDealerLocatorLoading={isDealerLocatorLoading}
        filtersDrawer={filtersDrawer}
        filtersCounter={filtersCounter}
        {...storeFeatureFlag}
      ></DealerLocatorTogglesAndFilter>
    );
  };

  return (
    <>
      <Conditional test={!(isFullMapMobileFeatureFlag && !isMD)}>
        {dealerLocatorToggleAndFilterContainer()}
      </Conditional>
      <ErrorBoundaryWrapper fallback={null}>
        <Conditional
          test={
            !isHidingMaps &&
            isMapToggleChecked &&
            !!filteredAndOrderedVisibleMapDealerStores
          }
        >
          <GoogleMapsLoader useSmallLoader={false}>
            <DealerLocatorMapContainer
              key={`map-container-${isExtendMapToggleChecked ? 'extended' : 'normal'}-${filteredAndOrderedVisibleMapDealerStores?.length || 0}`}
              userCountryCode={userCountryCode}
              dealerCode={dealerCode}
              dealerStores={filteredAndOrderedVisibleMapDealerStores}
              selectedStore={selectedStore}
              onSearchThisAreaClick={onSearchThisAreaClick}
              searchThisAreaBtnText={t('FYD_SEARCH_THIS_AREA')}
              loadingResults={isLoading}
              isStoreLocationPinEnhancementFlag={
                isStoreLocationPinEnhancementFlag
              }
              isMobile={!isMD}
              isMobileFullMapViewEnabled={isFullMapMobileFeatureFlag}
            />
          </GoogleMapsLoader>
        </Conditional>
      </ErrorBoundaryWrapper>
    </>
  );
};
